import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { fightingStyles } from '@character-creator/data';
import type { FightingStyle } from '@character-creator/types';
import { LinkedText } from '../components/LinkedText';

function formatClassName(classId: string): string {
  return classId.charAt(0).toUpperCase() + classId.slice(1);
}

export function FightingStylesReferencePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState<string | null>(null);

  const filteredStyles = useMemo(() => {
    let results = fightingStyles;

    if (classFilter) {
      results = results.filter((style) =>
        style.availableTo.includes(classFilter as 'fighter' | 'paladin' | 'ranger')
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(
        (style) =>
          style.name.toLowerCase().includes(query) ||
          style.description.toLowerCase().includes(query)
      );
    }

    return results;
  }, [searchQuery, classFilter]);

  const availableClasses = ['fighter', 'paladin', 'ranger'];

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background-card/95 backdrop-blur-sm border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/create"
              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
            >
              <BackIcon className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-text-primary">Fighting Styles Reference</h1>
          </div>
        </div>
      </header>

      {/* Search & Filters */}
      <div className="sticky top-[65px] z-10 bg-background-primary border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search fighting styles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-background-card border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-gold"
            />
          </div>

          {/* Class Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setClassFilter(null)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                classFilter === null
                  ? 'bg-accent-gold text-background-primary border-accent-gold'
                  : 'bg-background-card text-text-secondary border-border-default hover:border-text-muted'
              }`}
            >
              All Classes
            </button>
            {availableClasses.map((cls) => (
              <button
                key={cls}
                onClick={() => setClassFilter(cls)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  classFilter === cls
                    ? 'bg-accent-gold text-background-primary border-accent-gold'
                    : 'bg-background-card text-text-secondary border-border-default hover:border-text-muted'
                }`}
              >
                {formatClassName(cls)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Intro Text */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <p className="text-sm text-text-secondary">
          A fighting style represents a particular approach to combat that a warrior has mastered.
          Fighters can choose a fighting style at 1st level, while Paladins and Rangers gain this
          feature at 2nd level. You can't take a Fighting Style option more than once, even if you
          later get to choose again.
        </p>
        <p className="text-sm text-text-muted mt-2">
          Showing {filteredStyles.length} of {fightingStyles.length} fighting styles
        </p>
      </div>

      {/* Fighting Style List */}
      <main className="max-w-4xl mx-auto px-4 pb-8">
        {filteredStyles.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-card flex items-center justify-center border border-border-default">
              <SwordIcon className="w-8 h-8 text-text-muted" />
            </div>
            <p className="text-text-secondary">No fighting styles match your search.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setClassFilter(null);
              }}
              className="mt-4 px-4 py-2 text-accent-gold hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredStyles.map((style) => (
              <FightingStyleCard key={style.id} style={style} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

interface FightingStyleCardProps {
  style: FightingStyle;
}

function FightingStyleCard({ style }: FightingStyleCardProps) {
  return (
    <div id={style.id} className="bg-background-card border border-border-default rounded-lg p-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <h3 className="font-semibold text-text-primary text-lg">{style.name}</h3>
        <span className="text-xs text-text-muted shrink-0">{style.source}</span>
      </div>

      <p className="text-sm text-text-secondary mb-4">
        <LinkedText>{style.description}</LinkedText>
      </p>

      <div className="flex flex-wrap gap-2">
        {style.availableTo.map((cls) => (
          <Link
            key={cls}
            to={`/classes#${cls}`}
            className="px-2 py-1 text-xs font-medium bg-background-secondary text-text-muted rounded hover:bg-background-elevated hover:text-accent-gold transition-colors"
          >
            {formatClassName(cls)}
            {cls === 'fighter' ? ' (Lvl 1)' : ' (Lvl 2)'}
          </Link>
        ))}
      </div>
    </div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function SwordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 21l3.75-3.75M3 21l6-6m-6 6v-4.5m0 4.5h4.5M21 3l-7.5 7.5m0 0l-3 3m3-3l-3-3m3 3l3 3"
      />
    </svg>
  );
}
